import React, { useState, useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import { useProviderServices } from '../../../hooks/use-provider-services';
import { bulkLoadFulfillmentPlanIds } from '../../../store/bulkload/bulkload.api';
import Global from '../../../store/global-selectors';
import { validateBulkMarkAsShipped } from '../../../store/mark-as-shipped/mark-as-shipped.api';
import { BASE_MAS_PAGE } from '../constants';
import { MarkAsShippedContext } from '../context';
import { BuildFulfillmentPlanForMAS } from '../helper';
import style from '../style.module.less';

const messages = defineMessages({
  togglerTitle: {
    description: 'bulkMarkAsShipped.selectStoreToggler',
    defaultMessage: 'Select a Store',
    id: 'bKSY5B',
  },
  exclude: {
    description: 'bulkMarkAsAshipped.excludeOrders',
    defaultMessage: 'Exclude orders that cause errors',
    id: 'Mt8vOH',
  },
  floatLabelHeaderOrder: {
    description: 'Bulk mark as shipped example order number text',
    defaultMessage: 'Order #',
    id: 'Uf0DpL',
  },
  floatLabelHeaderTracking: {
    description: 'Bulk mark as shipped example tracking number text',
    defaultMessage: 'Tracking # (Optional)',
    id: 'gKwMQd',
  },
  floatLabelExample: {
    description: 'bulkMarkAsShipped.floatLabelExample',
    defaultMessage: 'Example:',
    id: 'axJpI7',
  },
  bulkMarkedAsShipCopyOne: {
    description: 'bulkMarkAsShipped.mainCopyOne',
    defaultMessage: 'Orders will be found in ShipStation using order number.',
    id: 'l4c1FN',
  },
  bulkMarkedAsShipCopyTwo: {
    description: 'bulkMarkAsShipped.mainCopyTwo',
    defaultMessage:
      'If you have a spreadsheet or list of order numbers (and optionally tracking numbers), you can copy and paste the list below. Order numbers and tracking numbers must be separated by a TAB character.',
    id: '8S5sA4',
  },
  bulkMarkedAsShipCopyThree: {
    description: 'bulkMarkAsShipped.mainCopyThree',
    defaultMessage:
      'After validating, the next step will allow you to mark the orders as shipped and optionally notify the marketplace and/or customers about the shipments.',
    id: '21pTPf',
  },
});

// Using template strings to support the placeholder new lines required by the mock, this also means any indentation will be requected.
// This is why there isn't "correct" indentation here.
const textAreaPlaceholder = (intl) =>
  `${intl.formatMessage(messages.floatLabelExample)}
${intl.formatMessage(messages.floatLabelHeaderOrder)}   ${intl.formatMessage(messages.floatLabelHeaderTracking)}
100001
100002   1Z468465432
100003   7801564586245`;

const selectAndSortStores = (state) => {
  const stores = Global.selectSellerActiveStores(state) || [];
  const sorted = stores.sort((a, b) => a.name.localeCompare(b.name));
  const initialSelectedStore = sorted.length ? sorted[0].storeGuid : null;
  return [initialSelectedStore, sorted];
};

const BulkMarkAsShipped = ({ closeModal, setPage }) => {
  const { setAndSelectFulfillmentPlanIds, setFulfillmentPlans } = useContext(MarkAsShippedContext);
  const services = useProviderServices();
  const intl = useIntl();
  const [initialSelectedStore, stores] = useSelector(selectAndSortStores);
  const [selectedStore, setSelectedStore] = useState(initialSelectedStore);
  const [bulkText, setBulkText] = useState('');
  const [excludeErroredOrders, setExcludeErroredOrders] = useState(false);
  const [validationData, setValidationData] = useState();
  const [networkError, setNetworkError] = useState();

  const onCheckboxTicked = useCallback(
    (checked) => {
      setExcludeErroredOrders(checked);
    },
    [setExcludeErroredOrders],
  );

  const onValidateBulk = useCallback(() => {
    setValidationData({}); // Clear an previous errors.
    const asyncFunc = async () => {
      try {
        const { erroredResponses, successfulResponses } = await validateBulkMarkAsShipped(bulkText, selectedStore, excludeErroredOrders);
        if (erroredResponses && erroredResponses.length > 0 && !excludeErroredOrders) {
          setValidationData({ erroredResponses });
          return;
        }
        if (successfulResponses) {
          const successfulFulfillmentPlanIds = successfulResponses.map((res) => res.fulfillmentPlanId);
          const { fulfillmentPlans, salesOrders } = await bulkLoadFulfillmentPlanIds(successfulFulfillmentPlanIds);
          const fulfillmentPlanMapById = fulfillmentPlans.reduce((fpById, plan) => {
            const { orderNumber, shipTos } = salesOrders.find((so) => so.salesOrderId === plan.salesOrdersFulfilledIds[0]);
            const fulfillmentPlanInSuccessResponse = successfulResponses.find((resp) => resp.fulfillmentPlanId === plan.fulfillmentPlanId);
            const { labelConfiguration, restrictions } = plan;
            const { serviceId } = labelConfiguration;
            const selectedService = services.find((service) => service.serviceId === serviceId);
            const carrierId = selectedService?.carrierId;
            if (!fulfillmentPlanInSuccessResponse) {
              return fpById;
            }
            const { trackingNumber } = fulfillmentPlanInSuccessResponse;
            return {
              ...fpById,
              [plan.fulfillmentPlanId]: {
                carrierId,
                ...BuildFulfillmentPlanForMAS(orderNumber, restrictions, shipTos, trackingNumber),
              },
            };
          }, {});
          setFulfillmentPlans(fulfillmentPlanMapById);
          setAndSelectFulfillmentPlanIds(successfulFulfillmentPlanIds);
          setPage(BASE_MAS_PAGE);
        }
      } catch (error) {
        setNetworkError(error);
      }
    };
    asyncFunc();
  }, [bulkText, excludeErroredOrders, selectedStore, services, setFulfillmentPlans, setPage, setAndSelectFulfillmentPlanIds]);

  const selectedStoreName = stores.find((store) => store.storeGuid === selectedStore) || {};
  const togglerTitle = selectedStoreName.name || intl.formatMessage(messages.togglerTitle);
  const { erroredResponses } = validationData || {};

  const textAreaRef = useRef();
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const { selectionStart, selectionEnd } = e.target;
        const textWithTab = `${bulkText.substring(0, selectionStart)}\t${bulkText.substring(selectionEnd, bulkText.length)}`;
        textAreaRef.current.focus();
        textAreaRef.current.value = textWithTab;

        textAreaRef.current.setSelectionRange(selectionStart + 1, selectionEnd + 1);
        setBulkText(textWithTab);
      }
    },
    [bulkText],
  );

  return (
    <>
      <ModalHeader closeModal={closeModal}>
        <div className={style['modal-header']}>
          <FormattedMessage defaultMessage='Mark as Shipped' description='bulkMarkAsShipped.header' id='MmAngT' />
        </div>
      </ModalHeader>
      <ModalBody className={style['bulk-body']}>
        <div className={style['bulk-instructions']}>
          <div>
            <FormattedMessage {...messages.bulkMarkedAsShipCopyOne} />
          </div>
          <div>
            <FormattedMessage {...messages.bulkMarkedAsShipCopyTwo} />
          </div>
          <div>
            <FormattedMessage {...messages.bulkMarkedAsShipCopyThree} />
          </div>
        </div>
        <div className={`${style['bulk-select-container']} ${style['flex-column']}`}>
          <span className={style['bulk-title']}>
            <FormattedMessage
              defaultMessage='Select a Store to mark orders from'
              description='bulkMarkAsShipped.selectStoreTitle'
              id='RnVMOn'
            />
          </span>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton value={selectedStore}>
            <DropdownMenuToggler className={style['bulk-select-button']}>{togglerTitle}</DropdownMenuToggler>
            {stores.map(({ storeGuid, name }) => (
              <DropdownMenuItem key={storeGuid} onClick={() => setSelectedStore(storeGuid)}>
                {name}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
        <div className={style['bulk-paste-container']}>
          <FloatTextArea
            ref={textAreaRef}
            onChange={setBulkText}
            onKeyDown={onKeyDown}
            placeholder={textAreaPlaceholder(intl)}
            value={bulkText}
          />
          <Checkbox checked={excludeErroredOrders} label={intl.formatMessage(messages.exclude)} onChange={onCheckboxTicked} />
        </div>
        <div className={style['bulk-error-container']}>
          <div className={style['bulk-title']}>
            <FormattedMessage
              defaultMessage='Any validation errors will be displayed below'
              description='bulkMarkAsShipped.validationErrorTitle'
              id='Nzegg8'
            />
          </div>
          <div className={style['bulk-errors']}>
            {erroredResponses && erroredResponses.length
              ? erroredResponses.map(({ error, orderNumber }) => (
                  <BannerMessageV2 key={orderNumber} className={style.error} content={error} status={BannerStatus.Error} />
                ))
              : null}
            {networkError ? (
              <BannerMessageV2
                content={
                  <FormattedMessage
                    defaultMessage='There was an error validating one or more of these Orders'
                    description='bulkMarkAsShipped.networkError'
                    id='zOlwy9'
                  />
                }
                status={BannerStatus.Error}
              />
            ) : null}
          </div>
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <Button onClick={closeModal} theme='tertiary'>
          <FormattedMessage defaultMessage='Cancel' description='bulkMarkAsShipped.cancel' id='8lffxm' />
        </Button>
        <Button className={style['mark-shipped-button']} disabled={bulkText.length === 0} onClick={onValidateBulk} theme='primary'>
          <FormattedMessage defaultMessage='Validate Orders' description='bulkMarkAsShipped.validate' id='VwjqPq' />
        </Button>
      </ModalFooter>
    </>
  );
};

BulkMarkAsShipped.propTypes = {
  closeModal: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
};

export default BulkMarkAsShipped;
